/**
 * Market Map Sidebar Component
 * Sidebar trái với dynamic tabs và danh sách markers
 */
import { useState, useMemo, useRef, useEffect } from 'react'
import {
  HiOutlineSearch,
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineAdjustments,
  HiOutlineChevronDown,
  HiOutlineMap,
  HiOutlineCollection,
  HiOutlineEye,
} from 'react-icons/hi'
import clsx from 'clsx'
import marketMapService from '../../services/marketMapService'
import PriceRangeFilter from './PriceRangeFilter'

// Max tabs to show before switching to dropdown
const MAX_VISIBLE_TABS = 3

function MarketMapSidebar({
  availableMaps = [],
  activeMapId,
  onMapChange,
  markers = [],
  selectedMarker,
  onMarkerClick,
  onViewDetail,
  mapInfo,
  loading,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000000000 })
  const [areaFilter, setAreaFilter] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter markers
  const filteredMarkers = useMemo(() => {
    return markers.filter((marker) => {
      const matchesSearch =
        marker.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        marker.address?.toLowerCase().includes(searchQuery.toLowerCase())

      // Price range filter
      let matchesPrice = true
      if (marker.price_value) {
        const price = marker.price_value
        matchesPrice = price >= priceRange.min && price <= priceRange.max
      }

      let matchesArea = true
      if (areaFilter !== 'all' && marker.area_m2) {
        const area = marker.area_m2
        if (areaFilter === 'under50') matchesArea = area < 50
        else if (areaFilter === '50to100') matchesArea = area >= 50 && area < 100
        else if (areaFilter === '100to200') matchesArea = area >= 100 && area < 200
        else if (areaFilter === 'above200') matchesArea = area >= 200
      }

      return matchesSearch && matchesPrice && matchesArea
    })
  }, [markers, searchQuery, priceRange, areaFilter])

  // Calculate statistics
  const stats = useMemo(() => {
    if (!markers.length) return null

    const prices = markers.filter((m) => m.price_value).map((m) => m.price_value)
    const areas = markers.filter((m) => m.area_m2).map((m) => m.area_m2)

    return {
      count: markers.length,
      minPrice: prices.length ? Math.min(...prices) : 0,
      maxPrice: prices.length ? Math.max(...prices) : 0,
      avgPrice: prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0,
      minArea: areas.length ? Math.min(...areas) : 0,
      maxArea: areas.length ? Math.max(...areas) : 0,
    }
  }, [markers])

  // Group maps by type for dropdown
  const groupedMaps = useMemo(() => {
    const groups = {}
    availableMaps.forEach((map) => {
      const type = map.map_type || 'other'
      if (!groups[type]) groups[type] = []
      groups[type].push(map)
    })
    return groups
  }, [availableMaps])

  // Get type label
  const getTypeLabel = (type) => {
    const labels = {
      cho_thue: 'Cho thuê',
      mua_ban: 'Mua bán',
      other: 'Khác',
    }
    return labels[type] || type
  }

  // Get icon for map type
  const getTypeIcon = (type) => {
    const icons = {
      cho_thue: HiOutlineHome,
      mua_ban: HiOutlineOfficeBuilding,
    }
    return icons[type] || HiOutlineMap
  }

  // Check if we should use dropdown mode
  const useDropdownMode = availableMaps.length > MAX_VISIBLE_TABS

  // Get current active map info
  const activeMap = availableMaps.find((m) => m.id === activeMapId)

  // Render tabs or dropdown
  const renderTabsOrDropdown = () => {
    if (useDropdownMode) {
      // Dropdown mode for many maps
      return (
        <div className="p-2 border-b border-gray-700" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className={clsx(
              'w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors',
              'bg-gray-800 hover:bg-gray-750 text-white'
            )}
          >
            <div className="flex items-center gap-2">
              {activeMap ? (
                <>
                  {(() => {
                    const Icon = getTypeIcon(activeMap.map_type)
                    return <Icon className="w-5 h-5 text-primary-400" />
                  })()}
                  <span className="font-medium truncate max-w-[180px]">{activeMap.name}</span>
                </>
              ) : (
                <>
                  <HiOutlineCollection className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-400">Chọn bản đồ</span>
                </>
              )}
            </div>
            <HiOutlineChevronDown
              className={clsx(
                'w-5 h-5 text-gray-400 transition-transform',
                showDropdown && 'rotate-180'
              )}
            />
          </button>

          {/* Dropdown menu */}
          {showDropdown && (
            <div className="absolute left-2 bg-[#111827] right-2 top-14 z-50 bg-gray-850 rounded-xl shadow-2xl border border-gray-600 max-h-100 overflow-y-auto">
              {/* Maps grouped by type */}
              {Object.entries(groupedMaps).map(([type, maps]) => (
                <div key={type}>
                  <div className="px-3 py-2 text-xs font-semibold text-primary-400 uppercase tracking-wider bg-gray-900/80 sticky top-0 backdrop-blur-sm border-b border-gray-700">
                    {getTypeLabel(type)}
                  </div>
                  <div className="py-1">
                    {maps.map((map) => {
                      const Icon = getTypeIcon(map.map_type)
                      return (
                        <button
                          key={map.id}
                          onClick={() => {
                            onMapChange(map.id)
                            setShowDropdown(false)
                          }}
                          className={clsx(
                            'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all',
                            activeMapId === map.id
                              ? 'bg-primary-600/30 text-primary-300 border-l-3 border-primary-500'
                              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
                          )}
                        >
                          <Icon className={clsx(
                            'w-4 h-4 flex-shrink-0',
                            activeMapId === map.id ? 'text-primary-400' : 'text-gray-500'
                          )} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{map.name}</div>
                            {map.description && (
                              <div className="text-xs text-gray-500 truncate">
                                {map.description}
                              </div>
                            )}
                          </div>
                          {map.marker_count > 0 && (
                            <span className={clsx(
                              'text-xs px-1.5 py-0.5 rounded-full',
                              activeMapId === map.id
                                ? 'bg-primary-500/30 text-primary-300'
                                : 'bg-gray-700 text-gray-400'
                            )}>
                              {map.marker_count}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }

    // Tab mode for few maps
    return (
      <div className="flex border-b border-gray-700 overflow-x-auto">
        {availableMaps.map((map) => {
          const Icon = getTypeIcon(map.map_type)
          return (
            <button
              key={map.id}
              onClick={() => onMapChange(map.id)}
              className={clsx(
                'flex-1 min-w-0 py-3 px-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5',
                activeMapId === map.id
                  ? 'bg-gray-800 text-white border-b-2 border-primary-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              )}
              title={map.name}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{map.name}</span>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="w-80 bg-gray-900 text-white flex flex-col h-full relative">
      {/* Tabs or Dropdown */}
      {renderTabsOrDropdown()}

      {/* Search & Filters */}
      <>
          <div className="p-3 border-b border-gray-700">
            {/* Search */}
            <div className="relative">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm..."
                className="w-full bg-gray-800 text-white placeholder-gray-500 rounded-lg pl-9 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors',
                  showFilters
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                <HiOutlineAdjustments className="w-4 h-4" />
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="mt-3 space-y-3">
                {/* Price Range Filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 block">Khoảng giá</label>
                  <PriceRangeFilter
                    minPrice={priceRange.min}
                    maxPrice={priceRange.max}
                    onChange={setPriceRange}
                    markers={markers}
                  />
                </div>

                {/* Area Filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Diện tích</label>
                  <select
                    value={areaFilter}
                    onChange={(e) => setAreaFilter(e.target.value)}
                    className="w-full bg-gray-800 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">Tất cả</option>
                    <option value="under50">Dưới 50 m²</option>
                    <option value="50to100">50 - 100 m²</option>
                    <option value="100to200">100 - 200 m²</option>
                    <option value="above200">Trên 200 m²</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Statistics */}
          {stats && (
            <div className="p-3 border-b border-gray-700 bg-gray-800/50">
              <div className="text-xs text-gray-400 mb-1">Thống kê</div>
              <div className="text-sm">
                <span className="text-white font-medium">{stats.count}</span>
                <span className="text-gray-400"> bất động sản</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Giá từ{' '}
                <span className="text-primary-400">
                  {marketMapService.formatPrice(stats.minPrice)}
                </span>
                {' - '}
                <span className="text-primary-400">
                  {marketMapService.formatPrice(stats.maxPrice)}
                </span>
              </div>
            </div>
          )}

          {/* Marker List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-gray-800 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : filteredMarkers.length > 0 ? (
              <div className="divide-y divide-gray-800">
                {filteredMarkers.map((marker) => (
                  <div
                    key={marker.id}
                    onClick={() => onMarkerClick(marker)}
                    className={clsx(
                      'w-full p-3 text-left transition-colors flex gap-3 cursor-pointer group',
                      selectedMarker?.id === marker.id
                        ? 'bg-gray-800 border-l-4 border-primary-500'
                        : 'hover:bg-gray-800/70'
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-700">
                      {marker.image_url ? (
                        <img
                          src={marker.image_url}
                          alt={marker.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <HiOutlineOfficeBuilding className="w-6 h-6 text-gray-500" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">
                        {marker.name}
                      </h3>
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {marker.address || 'Chưa có địa chỉ'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {marker.area_m2 && (
                          <span className="text-xs text-gray-500">
                            {marketMapService.formatArea(marker.area_m2)}
                          </span>
                        )}
                        <span className="text-sm font-medium text-primary-400">
                          {marker.price_text || marketMapService.formatPrice(marker.price_value)}
                        </span>
                      </div>
                    </div>

                    {/* View Detail Button */}
                    {onViewDetail && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onViewDetail(marker)
                        }}
                        className="self-center p-2 rounded-lg bg-gray-700 hover:bg-primary-600 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                        title="Xem chi tiết"
                      >
                        <HiOutlineEye className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                {mapInfo ? 'Không tìm thấy kết quả' : 'Chưa có dữ liệu'}
              </div>
            )}
          </div>
        </>

      {/* Map Info Footer */}
      {mapInfo && (
        <div className="p-3 border-t border-gray-700 bg-gray-800/50">
          <div className="text-xs text-gray-400">Bản đồ</div>
          <div className="text-sm text-white font-medium truncate">{mapInfo.name}</div>
          {mapInfo.description && (
            <div className="text-xs text-gray-500 truncate mt-0.5">
              {mapInfo.description}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MarketMapSidebar
